import { HttpErrorResponse } from '@angular/common/http';

/**
 * Translate a failed HTTP call into a human-readable message (UI guideline 4),
 * distinguishing an unreachable backend from a backend error (UI guideline 5).
 * Never surfaces raw status codes or technical jargon to the user.
 */
export function httpErrorToMessage(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    // status 0 means the request never reached the server (offline, DNS, CORS).
    if (err.status === 0) {
      return 'Could not reach the server. Check your connection and try again.';
    }
    if (err.status === 400 || err.status === 422) {
      // The backend sends ValidationException messages as a plain string body.
      const body = err.error;
      if (typeof body === 'string' && body.trim().length > 0) {
        return body.trim();
      }
      if (body && typeof body === 'object') {
        const msg = body['message'] || body['detail'] || body['error'];
        if (typeof msg === 'string' && msg.trim().length > 0) {
          return msg.trim();
        }
      }
      return 'The request was invalid. Please check your input and try again.';
    }
    if (err.status === 401 || err.status === 403) {
      return 'You do not have permission to perform this action.';
    }
    if (err.status === 404) {
      return 'The requested item could not be found.';
    }
    if (err.status === 409) {
      return 'The PI has already resolved a conflict with this paper and cannot be undone.';
    }
    if (err.status >= 500) {
      return 'The server ran into a problem. Please try again in a moment.';
    }
    return 'Something went wrong. Please try again.';
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}
